/** Layer plan and portrait crop (batch-003 `plan.ts`). The crop changes only the root viewBox. */
import { n } from '../../../shared/assets/hushStatic/construction';
import { AppearanceHash } from '../../../shared/utils/AppearanceHash';
import type { HumanoidAppearance } from '../parts/humanoid/HumanoidTypes';
import { legacyPainter } from '../parts/humanoid/NpcFinish';
import { buildWardrobePieces } from './pieces';
import { portraitCropFor, rigFor, type NpcPose } from './rig';
import { REVISION } from './types';
import type { HushAppearance } from './types';
import { validateAppearance } from './validate';

export function buildWardrobePlan(a: HushAppearance, pose: NpcPose = 'idle') {
  validateAppearance(a);
  const r = rigFor(a, pose);
  const config = a.humanoid.hushWardrobe!;
  const pieces = buildWardrobePieces(config, r.shape, {
    fit: true, alignToShape: true, frontHand: r.hands.frontHand, backHand: r.hands.backHand, painter: legacyPainter, phase: 0,
  });
  const logicalRecipeHash = AppearanceHash.generate(a);
  const layers = pieces.map((p, i) => ({
    id: p.id,
    asset: { assetId: p.id, recipeId: config.recipe.id, generatorId: 'hush.ghost_wardrobe.v1', generatorRevision: REVISION, logicalRecipeHash },
    rigAnchor: p.anchor.name, renderOrder: p.slot === 'head' ? 500 : i + 300, attachmentOffset: { x: 0, y: 0 }, scale: 1, rotationDeg: 0,
    actualMatrix: p.matrix, paintBounds: p.paintBounds, source: p.source, slot: p.slot,
  }));
  const supportPiece = config.recipe.params.look === 'pip' ? 'pip-paper-boat' : 'mallow-smooth-stone';
  return {
    recipeId: config.recipe.id,
    rigBinding: 'GhostRenderer/HumanoidExtras/BODY_RIGS',
    layers,
    faceClearance: { name: 'friendly_face', role: 'face-clearance' as const, bounds: r.faceClearance, gameplayAuthority: false as const },
    poseEnvelope: { x: 0, y: 0, width: 140, height: 120 },
    referenceBodyBounds: r.referenceBodyBounds,
    portraitCrop: portraitCropFor(a, pieces),
    facingPolicy: 'transform-geometry-and-anchors-together' as const,
    facingOwner: 'NPCRenderer whole-sprite flipX',
    sourceMirrorMatrix: [-1, 0, 0, 1, 140, 0],
    changesCollision: false as const,
    support: {
      mode: 'held-current-rig' as const, owner: 'ExtrasContext.frontHand', point: r.hands.frontHand, piece: supportPiece,
      visualContact: pieces.find((p) => p.id === supportPiece)!.anchor.point,
    },
    pieces,
  };
}

/** Extend the root viewBox to keep the cap and held prop in a bust portrait. Plain NPC SVG is returned unchanged. */
export function applyWardrobePortraitCrop(svg: string, a: HumanoidAppearance): string {
  if (!a.humanoid?.hushWardrobe) return svg;
  validateAppearance(a);
  const b = buildWardrobePlan(a).portraitCrop;
  return svg.replace(/viewBox="[^"]+"/, `viewBox="${n(b.x)} ${n(b.y)} ${n(b.width)} ${n(b.height)}"`);
}
